import React from 'react'
import { Tooltip } from '@surecompanies/core_components'

import { ReviewQueueColumn } from './helpers'
import styles from './ReviewQueue.module.scss'

/**
 * Sticky header row for one Required-action tab, laid out from the same
 * `gridTemplateColumns` its rows use — the header and the column it names come
 * from one array in helpers.ts and cannot drift.
 *
 * Headers are labels only today (spec D4): `ReviewQueueParams` has no `sortBy`
 * and the backend returns a fixed `sortDate` order, so no column carries a
 * `sortKey` and every cell renders as a static label. Issue 10 adds `sortKey`s
 * to the column defs and passes `sortBy` / `sortOrder` / `onToggleSort` — the
 * sortable branch below then renders those columns as native <button>s with
 * `aria-sort`, so enabling sort is a data + prop change, not a rewrite.
 *
 * Mirrors TransactionDetails/TransactionsTable/TableHead.tsx.
 */

type SortOrder = 'asc' | 'desc'

interface TableHeadProps {
  columns: readonly ReviewQueueColumn[]
  gridTemplateColumns: string
  sortBy?: string
  sortOrder?: SortOrder
  onToggleSort?: (sortKey: string) => void
}

/**
 * Per-column modifier classes, shared by the sortable and static branches so a
 * new flag is added in one place rather than two.
 */
const columnModifiers = (col: ReviewQueueColumn): string =>
  `${col.numeric ? ` ${styles.headerNum}` : ''}${col.wrap ? ` ${styles.headerCellWrap}` : ''}`

const SortIndicator: React.FC<{ dir: SortOrder | null }> = ({ dir }) => (
  <span className={styles.sortIndicator} aria-hidden="true">
    <span className={dir === 'asc' ? styles.sortArrowActive : undefined}>
      ▲
    </span>
    <span className={dir === 'desc' ? styles.sortArrowActive : undefined}>
      ▼
    </span>
  </span>
)

const TableHead: React.FC<TableHeadProps> = ({
  columns,
  gridTemplateColumns,
  sortBy,
  sortOrder,
  onToggleSort,
}) => (
  <div className={styles.headerRow} style={{ gridTemplateColumns }}>
    {columns.map((col) => {
      // A column explaining what it actually holds (Reference is
      // `token ?? transactionId`; Expected may be stale) wears a dotted
      // underline so the tooltip is discoverable.
      const label = col.tooltip ? (
        <Tooltip title={col.tooltip}>
          <span className={styles.headerHint}>{col.label}</span>
        </Tooltip>
      ) : (
        col.label
      )

      // Sortable column — reachable once issue 10 supplies both a `sortKey` and
      // an `onToggleSort`.
      if (col.sortKey && onToggleSort) {
        const sortKey = col.sortKey
        const isActive = sortBy === sortKey
        return (
          <button
            key={col.label}
            type="button"
            className={`${styles.headerCell}${columnModifiers(col)}`}
            aria-sort={
              isActive
                ? sortOrder === 'asc'
                  ? 'ascending'
                  : 'descending'
                : 'none'
            }
            onClick={() => onToggleSort(sortKey)}
          >
            <span>{label}</span>
            <SortIndicator dir={isActive ? (sortOrder ?? null) : null} />
          </button>
        )
      }

      return (
        <span
          key={col.label}
          className={`${styles.headerCellStatic}${columnModifiers(col)}`}
        >
          {label}
        </span>
      )
    })}
  </div>
)

export default TableHead
